import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/contexts/ToastContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatCard } from "@/components/ui/stat-card";
import { CardStatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { libraryCardsApi, type LibraryCard } from "@/lib/api/library-cards";
import {
  CheckCircle2,
  Clock,
  Package,
  AlertCircle,
  Eye,
  User,
  Mail,
  Phone,
  Loader2,
  Printer,
  CreditCard,
  ShieldOff,
  ArrowRight,
} from "lucide-react";

/* ── Constants ──────────────────────────────────────────── */

const statusOptions: {
  value: LibraryCard["status"];
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { value: "PENDING", label: "Pending", icon: Clock },
  { value: "PROCESSING", label: "Processing", icon: Clock },
  { value: "READY", label: "Ready for Pickup", icon: Package },
  { value: "PICKED_UP", label: "Picked Up", icon: CheckCircle2 },
  { value: "EXPIRED", label: "Expired", icon: AlertCircle },
];

const statusTransitions: Record<LibraryCard["status"], LibraryCard["status"][]> = {
  PENDING: ["PROCESSING"],
  PROCESSING: ["READY"],
  READY: ["PICKED_UP", "EXPIRED"],
  PICKED_UP: [],
  EXPIRED: ["PICKED_UP"],
};

const formatDateOnly = (dateString?: string) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDaysUntilDeadline = (deadline?: string) => {
  if (!deadline) return null;
  const diffTime = new Date(deadline).getTime() - Date.now();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const cardUserName = (card: LibraryCard) =>
  [card.user?.firstName, card.user?.lastName].filter(Boolean).join(" ") || "—";

/* ── Component ─────────────────────────────────────────── */

export default function CardProcessingPage() {
  const { user } = useAuth();
  const toast = useToast();

  const [cards, setCards] = useState<LibraryCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<LibraryCard["status"] | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [updating, setUpdating] = useState(false);

  const [selectedCard, setSelectedCard] = useState<LibraryCard | null>(null);
  const [showCardDetail, setShowCardDetail] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState<LibraryCard["status"]>("PROCESSING");
  const [notes, setNotes] = useState("");
  const [customDeadline, setCustomDeadline] = useState("");

  const { isTenantAdmin } = useAccessControl();
  const hasCardPermission = user?.permissions?.some(
    (p: string) =>
      p === "cards.process" ||
      p === "cards.markPickup" ||
      p === "cards.print" ||
      p === "cards.*" ||
      p === "*"
  );
  const canAccess = isTenantAdmin || hasCardPermission;

  useEffect(() => {
    if (!canAccess) return;
    loadCards();
  }, [canAccess, statusFilter, searchQuery]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const data = await libraryCardsApi.getCardsForProcessing({
        status: statusFilter !== "all" ? statusFilter : undefined,
        search: searchQuery || undefined,
      });
      setCards(data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load cards");
    } finally {
      setLoading(false);
    }
  };

  const handleViewCard = (card: LibraryCard) => {
    setSelectedCard(card);
    setShowCardDetail(true);
  };

  const handleUpdateStatusClick = (card?: LibraryCard) => {
    if (card) {
      setSelectedCard(card);
      setSelectedIds(new Set([card.id]));
    } else {
      // Bulk: do not reuse a previously opened row/detail card — update all selected rows
      setSelectedCard(null);
    }
    const selectedList = card ? [card] : cards.filter((c) => selectedIds.has(c.id));
    const firstStatus = selectedList[0]?.status;
    const commonStatus =
      selectedList.length > 0 && selectedList.every((c) => c.status === firstStatus)
        ? firstStatus
        : null;
    if (commonStatus) {
      const transitions = statusTransitions[commonStatus] || [];
      if (transitions.length > 0) setNewStatus(transitions[0]);
    }
    setShowStatusUpdate(true);
  };

  const handleUpdateStatus = async () => {
    const cardIds =
      selectedIds.size > 0 ? Array.from(selectedIds) : selectedCard ? [selectedCard.id] : [];
    if (cardIds.length === 0) return;
    try {
      setUpdating(true);
      if (newStatus === "PICKED_UP") {
        await libraryCardsApi.markPickedUp(cardIds);
      } else {
        await libraryCardsApi.updateCardStatus({
          cardIds,
          status: newStatus,
          notes: notes || undefined,
          customPickupDeadline: customDeadline || undefined,
        });
      }
      toast.success(
        `Updated ${cardIds.length} card(s) to ${statusOptions.find((s) => s.value === newStatus)?.label}`
      );
      setSelectedIds(new Set());
      setSelectedCard(null);
      setShowStatusUpdate(false);
      setNotes("");
      setCustomDeadline("");
      await loadCards();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update cards");
    } finally {
      setUpdating(false);
    }
  };

  const handleCheckExpired = async () => {
    try {
      setUpdating(true);
      const result = await libraryCardsApi.checkExpiredPickups();
      toast.success(result.message);
      await loadCards();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to check expired cards");
    } finally {
      setUpdating(false);
    }
  };

  /* ── Stats ───────────────────────────────────────────── */

  const statCounts = useMemo(() => {
    const m: Record<string, number> = { total: cards.length };
    for (const c of cards) m[c.status] = (m[c.status] || 0) + 1;
    return m;
  }, [cards]);

  /* ── Current transitions (single row, or bulk when every selected card shares status) ─ */

  const statusForTransitionDialog = (() => {
    if (selectedCard) return selectedCard.status;
    const picked = cards.filter((c) => selectedIds.has(c.id));
    if (picked.length === 0) return null;
    const first = picked[0].status;
    return picked.every((c) => c.status === first) ? first : null;
  })();
  const availableTransitions = statusForTransitionDialog
    ? statusTransitions[statusForTransitionDialog] || []
    : [];

  /* ── Columns ─────────────────────────────────────────── */

  const columns = useMemo<DataTableColumn<LibraryCard>[]>(
    () => [
      {
        id: "patron",
        header: "Patron",
        cell: (card) => (
          <div>
            <p className="font-medium text-slate-900">{cardUserName(card)}</p>
            <p className="mt-0.5 text-xs text-slate-500">{card.user?.email}</p>
          </div>
        ),
      },
      {
        id: "cardNumber",
        header: "Card No.",
        cell: (card) => (
          <span className="font-mono text-xs text-slate-600">{card.virtualCardNumber}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: (card) => <CardStatusBadge status={card.status} />,
      },
      {
        id: "processing",
        header: "Processing",
        cell: (card) => (
          <span className="text-slate-500">{formatDateOnly(card.processingStartedAt)}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: "ready",
        header: "Ready",
        cell: (card) => <span className="text-slate-500">{formatDateOnly(card.readyAt)}</span>,
        hideOnMobile: true,
      },
      {
        id: "deadline",
        header: "Pickup Deadline",
        cell: (card) => {
          if (!card.pickupDeadline) return <span className="text-slate-400">—</span>;
          const days = getDaysUntilDeadline(card.pickupDeadline);
          const isExpired = card.status === "EXPIRED" || (days !== null && days < 0);
          return (
            <span
              className={
                isExpired
                  ? "font-medium text-red-600"
                  : days !== null && days <= 3
                    ? "text-amber-600"
                    : "text-slate-500"
              }
            >
              {formatDateOnly(card.pickupDeadline)}
              {days !== null && !isExpired && (
                <span className="ml-1 text-xs">
                  ({days} {days === 1 ? "day" : "days"})
                </span>
              )}
            </span>
          );
        },
        hideOnMobile: true,
      },
      {
        id: "fine",
        header: "Fine",
        align: "right",
        cell: (card) =>
          card.latePickupFine ? (
            <span className="font-medium text-red-600">₦{card.latePickupFine.toFixed(2)}</span>
          ) : (
            <span className="text-slate-400">—</span>
          ),
        hideOnMobile: true,
      },
      {
        id: "actions",
        header: "",
        align: "right",
        cell: (card) => {
          const transitions = statusTransitions[card.status] || [];
          return (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 touch-manipulation"
                onClick={() => handleViewCard(card)}
                aria-label="View details"
              >
                <Eye className="h-4 w-4" />
              </Button>
              {transitions.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="touch-manipulation"
                  onClick={() => handleUpdateStatusClick(card)}
                >
                  Update
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  /* ── Mobile card ──────────────────────────────────────── */

  const mobileCard = (card: LibraryCard, idx: number) => {
    const days = getDaysUntilDeadline(card.pickupDeadline);
    const transitions = statusTransitions[card.status] || [];
    return (
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex items-start justify-between gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium tabular-nums text-slate-400">{idx + 1}.</span>
              <p className="font-heading text-sm font-semibold text-slate-900">
                {cardUserName(card)}
              </p>
            </div>
            <p className="mt-0.5 font-mono text-xs text-slate-500">{card.virtualCardNumber}</p>
          </div>
          <CardStatusBadge status={card.status} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
          {card.processingStartedAt && (
            <span>Started: {formatDateOnly(card.processingStartedAt)}</span>
          )}
          {card.pickupDeadline && (
            <span className={days !== null && days < 0 ? "font-medium text-red-600" : ""}>
              Deadline: {formatDateOnly(card.pickupDeadline)}
            </span>
          )}
          {card.latePickupFine && (
            <span className="font-medium text-red-600">
              Fine: ₦{card.latePickupFine.toFixed(2)}
            </span>
          )}
        </div>
        <div className="flex gap-2 border-t border-slate-100 px-4 py-2.5">
          <Button
            variant="outline"
            size="sm"
            className="h-10 flex-1 touch-manipulation"
            onClick={() => handleViewCard(card)}
          >
            <Eye className="mr-1.5 h-4 w-4" />
            Details
          </Button>
          {transitions.length > 0 && (
            <Button
              size="sm"
              className="h-10 flex-1 touch-manipulation"
              onClick={() => handleUpdateStatusClick(card)}
            >
              Update Status
            </Button>
          )}
        </div>
      </div>
    );
  };

  /* ── Access denied ───────────────────────────────────── */

  if (!canAccess) {
    return (
      <EmptyState
        icon={<ShieldOff className="h-6 w-6 text-red-400" />}
        title="Access Denied"
        description="You need permission to process library cards. Contact your administrator."
      />
    );
  }

  /* ── Render ──────────────────────────────────────────── */

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Card Processing"
        description="Manage physical library card processing from generation to pickup"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Card Processing" }]}
        showSearch={false}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="min-h-11 touch-manipulation sm:min-h-10"
              onClick={handleCheckExpired}
              disabled={updating}
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              Check Expired
            </Button>
            {selectedIds.size > 0 && (
              <>
                <Button
                  variant="outline"
                  className="min-h-11 touch-manipulation sm:min-h-10"
                  onClick={() =>
                    window.open(
                      `/admin/cards/print?cardIds=${Array.from(selectedIds).join(",")}`,
                      "_blank"
                    )
                  }
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Print {selectedIds.size}
                </Button>
                <Button
                  className="min-h-11 touch-manipulation sm:min-h-10"
                  onClick={() => handleUpdateStatusClick()}
                  disabled={updating}
                >
                  Update {selectedIds.size}
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* Workflow guide */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/10 bg-primary/5 px-4 py-3 text-sm">
        <span className="mr-1 font-medium text-primary">Workflow:</span>
        <Badge className="bg-slate-100 text-slate-700 shadow-none hover:bg-slate-100">
          Pending
        </Badge>
        <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
        <Badge className="bg-blue-50 text-blue-700 shadow-none hover:bg-blue-50">Processing</Badge>
        <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
        <Badge className="bg-emerald-50 text-emerald-700 shadow-none hover:bg-emerald-50">
          Ready
        </Badge>
        <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
        <Badge className="bg-purple-50 text-purple-700 shadow-none hover:bg-purple-50">
          Picked Up
        </Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          icon={<CreditCard className="h-5 w-5" />}
          label="Total Cards"
          value={statCounts.total || 0}
        />
        <StatCard
          icon={<Clock className="h-5 w-5" />}
          label="Pending"
          value={statCounts.PENDING || 0}
          iconBg="bg-slate-100 text-slate-600"
        />
        <StatCard
          icon={<Package className="h-5 w-5" />}
          label="Ready"
          value={statCounts.READY || 0}
          iconBg="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          icon={<AlertCircle className="h-5 w-5" />}
          label="Expired"
          value={statCounts.EXPIRED || 0}
          iconBg="bg-red-50 text-red-600"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="min-h-11 w-full touch-manipulation sm:min-h-9 sm:w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative w-full sm:w-[260px]">
          <Input
            placeholder="Search by name, email, card no…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="min-h-11 touch-manipulation sm:min-h-9"
          />
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={cards}
        rowKey={(c) => c.id}
        loading={loading}
        selectable
        selectedIds={selectedIds}
        onSelectChange={setSelectedIds}
        showSerialNumber
        mobileCard={mobileCard}
        emptyState={
          <EmptyState
            icon={<CreditCard className="h-6 w-6 text-slate-400" />}
            title="No cards found"
            description={
              searchQuery || statusFilter !== "all"
                ? "Try adjusting your filters."
                : "Library cards will appear here when patrons register."
            }
          />
        }
      />

      {/* ── Card Detail Modal ─────────────────────────────── */}
      <Dialog open={showCardDetail} onOpenChange={setShowCardDetail}>
        <DialogContent className="flex max-h-[min(90vh,680px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="shrink-0 border-b border-slate-100 px-6 pb-4 pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="font-heading text-lg font-semibold text-slate-900">
                  Card Details
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-slate-500">
                  Full information about this library card.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {selectedCard && (
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-400">Card Number</Label>
                  <p className="font-mono text-lg font-semibold text-slate-900">
                    {selectedCard.virtualCardNumber}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Status</Label>
                  <div className="mt-1">
                    <CardStatusBadge status={selectedCard.status} />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <User className="h-4 w-4" /> Patron
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400">Name</Label>
                    <p className="text-sm font-medium text-slate-900">
                      {cardUserName(selectedCard)}
                    </p>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1 text-xs text-slate-400">
                      <Mail className="h-3 w-3" /> Email
                    </Label>
                    <p className="text-sm text-slate-900">{selectedCard.user?.email}</p>
                  </div>
                  {selectedCard.user?.phone && (
                    <div>
                      <Label className="flex items-center gap-1 text-xs text-slate-400">
                        <Phone className="h-3 w-3" /> Phone
                      </Label>
                      <p className="text-sm text-slate-900">{selectedCard.user.phone}</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Clock className="h-4 w-4" /> Timeline
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Generated</span>
                    <span className="text-slate-900">
                      {formatDateTime(selectedCard.generatedAt)}
                    </span>
                  </div>
                  {selectedCard.processingStartedAt && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Processing</span>
                      <span className="text-slate-900">
                        {formatDateTime(selectedCard.processingStartedAt)}
                      </span>
                    </div>
                  )}
                  {selectedCard.readyAt && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ready</span>
                      <span className="text-emerald-600">
                        {formatDateTime(selectedCard.readyAt)}
                      </span>
                    </div>
                  )}
                  {selectedCard.pickupDeadline && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Deadline</span>
                      <span className="text-amber-600">
                        {formatDateTime(selectedCard.pickupDeadline)}
                      </span>
                    </div>
                  )}
                  {selectedCard.pickedUpAt && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Picked up</span>
                      <span className="text-purple-600">
                        {formatDateTime(selectedCard.pickedUpAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedCard.notes && (
                <div>
                  <Label className="text-xs text-slate-400">Admin Notes</Label>
                  <p className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    {selectedCard.notes}
                  </p>
                </div>
              )}

              {selectedCard.latePickupFine != null && selectedCard.latePickupFine > 0 && (
                <div>
                  <Label className="text-xs text-slate-400">Late Pickup Fine</Label>
                  <p className="mt-1 text-lg font-semibold text-red-600">
                    ₦{selectedCard.latePickupFine.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="min-h-11 w-full touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={() => setShowCardDetail(false)}
            >
              Close
            </Button>
            {selectedCard && (statusTransitions[selectedCard.status] || []).length > 0 && (
              <Button
                className="min-h-11 w-full touch-manipulation sm:min-h-10 sm:w-auto"
                onClick={() => {
                  setShowCardDetail(false);
                  handleUpdateStatusClick(selectedCard);
                }}
              >
                Update Status
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Status Update Modal ────────────────────────────── */}
      <Dialog open={showStatusUpdate} onOpenChange={setShowStatusUpdate}>
        <DialogContent className="flex max-h-[min(90vh,520px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogHeader className="shrink-0 border-b border-slate-100 px-6 pb-4 pt-6">
            <DialogTitle className="font-heading text-lg font-semibold text-slate-900">
              Update Card Status
            </DialogTitle>
            <DialogDescription className="mt-0.5 text-sm text-slate-500">
              {selectedCard ? `Updating 1 card` : `Updating ${selectedIds.size} card(s)`}
            </DialogDescription>
            {selectedIds.size > 1 && !statusForTransitionDialog ? (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Bulk status changes require all selected cards to have the same current status.
                Adjust your selection or update cards in smaller groups.
              </p>
            ) : null}
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">New Status</Label>
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableTransitions.map((status) => {
                    const opt = statusOptions.find((o) => o.value === status);
                    return (
                      <SelectItem key={status} value={status}>
                        {opt?.label || status}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {availableTransitions.length === 0 && (
                <p className="text-xs text-slate-400">No valid transitions for current status.</p>
              )}
            </div>

            {newStatus === "READY" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">
                  Custom Pickup Deadline
                  <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
                </Label>
                <Input
                  type="date"
                  value={customDeadline}
                  onChange={(e) => setCustomDeadline(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
                <p className="text-xs text-slate-400">Default is 7 days from now.</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">
                Notes
                <span className="ml-1 text-xs font-normal text-slate-400">(optional)</span>
              </Label>
              <Textarea
                placeholder="Add notes about this change…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="min-h-11 w-full touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={() => {
                setShowStatusUpdate(false);
                setNotes("");
                setCustomDeadline("");
              }}
            >
              Cancel
            </Button>
            <Button
              className="min-h-11 w-full touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={handleUpdateStatus}
              disabled={updating || availableTransitions.length === 0}
            >
              {updating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Updating…
                </>
              ) : (
                "Update Status"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
